// src/pages/BulkReps.js
import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, query, where } from 'firebase/firestore';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { db, auth } from '../firebase';
import { DEFAULT_COMP_PLAN } from '../data/compPlans';
import * as XLSX from 'xlsx';

export default function BulkReps() {
  const [compPlans, setCompPlans] = useState([DEFAULT_COMP_PLAN]);
  const [selectedPlan, setSelectedPlan] = useState('synergy_1_31_26');
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, errors: [] });
  const [done, setDone] = useState(false);

  useEffect(() => {
    async function load() {
      const snap = await getDocs(collection(db, 'compPlans'));
      const dbPlans = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      if (dbPlans.length) setCompPlans([DEFAULT_COMP_PLAN, ...dbPlans]);
    }
    load();
  }, []);

  function handleFile(e) {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      const wb = XLSX.read(new Uint8Array(ev.target.result), { type: 'array' });
      // Try Master Roster sheet first, fall back to first sheet
      const sheetName = wb.SheetNames.find(s => s.toLowerCase().includes('master')) || wb.SheetNames[0];
      const ws = wb.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });

      const reps = [];
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        // Detect header row: has First/Last/Email/Sales ID pattern
        if (String(row[1]).toLowerCase().includes('first') && String(row[2]).toLowerCase().includes('last')) continue;
        // Skip section headers and empty rows
        const first = String(row[1] || '').trim();
        const last  = String(row[2] || '').trim();
        const email = String(row[3] || '').trim();
        const salesId = String(row[4] || '').trim();
        if (!first || !last || !email || !salesId) continue;
        if (first === 'First' || first === 'Agent') continue;
        if (!email.includes('@')) continue;
        reps.push({
          name: `${first} ${last}`,
          email: email.toLowerCase(),
          agentCode: salesId,
          role: 'Agent',
        });
      }

      // Deduplicate by email
      const seen = new Set();
      const unique = reps.filter(r => {
        if (seen.has(r.email)) return false;
        seen.add(r.email);
        return true;
      });

      setPreview(unique);
      setDone(false);
      setProgress({ done: 0, total: 0, errors: [] });
    };
    reader.readAsArrayBuffer(file);
  }

  async function runImport() {
    if (!preview) return;
    setImporting(true);
    setDone(false);
    const errors = [];
    let done = 0;

    // Get existing emails to skip duplicates
    const existingSnap = await getDocs(collection(db, 'users'));
    const existingEmails = new Set(existingSnap.docs.map(d => d.data().email?.toLowerCase()));

    setProgress({ done: 0, total: preview.length, errors: [] });

    for (const rep of preview) {
      if (existingEmails.has(rep.email.toLowerCase())) {
        done++;
        setProgress(p => ({ ...p, done }));
        continue;
      }
      try {
        // Generate temp password: first name + last 4 of agent code + !
        const tempPassword = rep.name.split(' ')[0] + (rep.agentCode || '').slice(-4) + '!Tmp';
        const cred = await createUserWithEmailAndPassword(auth, rep.email, tempPassword);
        await addDoc(collection(db, 'users'), {
          name: rep.name,
          email: rep.email,
          role: rep.role,
          compPlanId: selectedPlan,
          agentCode: rep.agentCode,
          phone: '',
          uid: cred.user.uid,
          active: true,
          tempPassword, // stored so admin can share with rep
        });
        done++;
        setProgress(p => ({ ...p, done }));
      } catch (err) {
        errors.push({ name: rep.name, email: rep.email, error: err.message });
        done++;
        setProgress(p => ({ ...p, done, errors: [...p.errors, { name: rep.name, error: err.message }] }));
      }
    }
    setImporting(false);
    setDone(true);
  }

  const pct = progress.total > 0 ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <div style={S.page}>
      <div style={S.header}>
        <h1 style={S.title}>Bulk upload reps</h1>
        <p style={S.sub}>Upload your Regional Locations Reps spreadsheet to create all accounts at once</p>
      </div>

      <div style={S.card}>
        <div style={S.cardTitle}>Upload roster file</div>
        <div style={S.infoBox}>
          Accepts your <strong>Regional_Locations_Reps.xlsx</strong> file. Reads the Master Roster sheet automatically — pulls first name, last name, email, and sales ID for each rep.
        </div>

        <div style={S.row}>
          <div style={{ flex: 1 }}>
            <label style={S.label}>Default comp plan for all imported reps</label>
            <select style={S.input} value={selectedPlan} onChange={e => setSelectedPlan(e.target.value)}>
              {compPlans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <div style={S.hint}>You can update individual reps after import</div>
          </div>
        </div>

        <div style={S.uploadArea} onClick={() => document.getElementById('roster-input').click()}>
          <div style={S.upIcon}>↑</div>
          <div style={S.upText}>Click to upload your rep roster .xlsx file</div>
          <input id="roster-input" type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleFile} />
        </div>
      </div>

      {preview && !done && (
        <>
          <div style={S.card}>
            <div style={S.cardTitle}>Preview — {preview.length} reps found</div>
            <div style={S.statsRow}>
              <div style={S.stat}><div style={S.statLbl}>Total reps</div><div style={{ ...S.statVal, color: '#fff' }}>{preview.length}</div></div>
              <div style={S.stat}><div style={S.statLbl}>Comp plan</div><div style={{ ...S.statVal, color: '#00d4a0', fontSize: 14 }}>{compPlans.find(p => p.id === selectedPlan)?.name}</div></div>
              <div style={S.stat}><div style={S.statLbl}>Temp passwords</div><div style={{ ...S.statVal, color: '#f5a623', fontSize: 13 }}>Auto-generated</div></div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={S.table}>
                <thead><tr>{['Name', 'Email', 'Agent code', 'Role'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {preview.slice(0, 10).map((r, i) => (
                    <tr key={i}>
                      <td style={S.td}>{r.name}</td>
                      <td style={{ ...S.td, color: '#666' }}>{r.email}</td>
                      <td style={{ ...S.td, color: '#00d4a0' }}>{r.agentCode}</td>
                      <td style={{ ...S.td, color: '#888' }}>{r.role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {preview.length > 10 && <div style={{ fontSize: 11, color: '#444', padding: '8px 0', textAlign: 'center' }}>+ {preview.length - 10} more reps</div>}
            </div>
            <div style={S.passwordNote}>
              Temp passwords are auto-generated as: <code style={{ color: '#00d4a0' }}>FirstName + last4ofAgentCode + !Tmp</code><br />
              e.g. Tarik Garrett (FCC001) → <code style={{ color: '#00d4a0' }}>TarikC001!Tmp</code><br />
              Reps should change their password on first login. You can view temp passwords in Firestore under the users collection.
            </div>
          </div>

          {importing && (
            <div style={S.card}>
              <div style={S.cardTitle}>Importing...</div>
              <div style={S.progressBar}>
                <div style={{ ...S.progressFill, width: `${pct}%` }} />
              </div>
              <div style={S.progressText}>{progress.done} of {progress.total} reps created ({pct}%)</div>
              {progress.errors.length > 0 && (
                <div style={S.errorList}>
                  {progress.errors.slice(-3).map((e, i) => <div key={i} style={S.errorItem}>⚠ {e.name}: {e.error}</div>)}
                </div>
              )}
            </div>
          )}

          <div style={S.actions}>
            <button style={S.importBtn} onClick={runImport} disabled={importing}>
              {importing ? `Creating accounts... ${pct}%` : `Create ${preview.length} rep accounts`}
            </button>
          </div>
        </>
      )}

      {done && (
        <div style={S.successCard}>
          <div style={S.successIcon}>✓</div>
          <div style={S.successTitle}>Import complete</div>
          <div style={S.successSub}>{progress.done - progress.errors.length} reps created successfully{progress.errors.length > 0 ? ` · ${progress.errors.length} skipped (already exist or error)` : ''}</div>
          {progress.errors.length > 0 && (
            <div style={S.errorList}>
              {progress.errors.map((e, i) => <div key={i} style={S.errorItem}>{e.name}: {e.error}</div>)}
            </div>
          )}
          <div style={S.nextSteps}>
            Next: go to <strong>Reps</strong> to verify accounts, then upload your Spectrum CSV in <strong>Import</strong> — commissions will now calculate automatically.
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  page: { padding: '20px 16px', color: '#fff', width: '100%', boxSizing: 'border-box' },
  header: { marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 600, margin: 0, letterSpacing: '-0.3px' },
  sub: { fontSize: 13, color: '#555', margin: '4px 0 0' },
  card: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 12, padding: '20px 24px', marginBottom: 16 },
  cardTitle: { fontSize: 12, color: '#888', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 },
  infoBox: { background: '#0f0f18', border: '1px solid #1e1e2a', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: '#888', marginBottom: 16, lineHeight: 1.6 },
  row: { marginBottom: 16 },
  label: { display: 'block', fontSize: 11, color: '#666', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 500 },
  input: { width: '100%', maxWidth: 320, background: '#1a1a24', border: '1px solid #2a2a3a', borderRadius: 8, padding: '9px 12px', color: '#fff', fontSize: 13, outline: 'none', boxSizing: 'border-box' },
  hint: { fontSize: 11, color: '#444', marginTop: 4 },
  uploadArea: { border: '1px dashed #2a2a3a', borderRadius: 10, padding: '36px', textAlign: 'center', cursor: 'pointer' },
  upIcon: { fontSize: 28, color: '#00d4a0', marginBottom: 10 },
  upText: { fontSize: 14, color: '#888' },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 16 },
  stat: { background: '#0f0f18', borderRadius: 8, padding: '10px 12px' },
  statLbl: { display: 'block', fontSize: 10, color: '#555', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 },
  statVal: { fontSize: 22, fontWeight: 600 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
  th: { textAlign: 'left', padding: '6px 10px', color: '#555', fontWeight: 500, borderBottom: '1px solid #1e1e2a', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em' },
  td: { padding: '8px 10px', color: '#ccc', borderBottom: '1px solid #0f0f18' },
  passwordNote: { marginTop: 14, padding: '12px 14px', background: '#0f0f18', borderRadius: 8, fontSize: 12, color: '#666', lineHeight: 1.7 },
  actions: { display: 'flex', gap: 12, alignItems: 'center' },
  importBtn: { background: '#00d4a0', border: 'none', borderRadius: 8, padding: '12px 32px', color: '#000', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  progressBar: { background: '#1a1a24', borderRadius: 6, height: 6, marginBottom: 8, overflow: 'hidden' },
  progressFill: { background: '#00d4a0', height: '100%', borderRadius: 6, transition: 'width .3s' },
  progressText: { fontSize: 13, color: '#888' },
  errorList: { marginTop: 10, display: 'flex', flexDirection: 'column', gap: 4 },
  errorItem: { fontSize: 11, color: '#f5a623', background: '#2a2010', padding: '4px 8px', borderRadius: 5 },
  successCard: { background: '#13131a', border: '1px solid #2a5a3a', borderRadius: 12, padding: '32px', textAlign: 'center' },
  successIcon: { fontSize: 40, color: '#00d4a0', marginBottom: 12 },
  successTitle: { fontSize: 20, fontWeight: 600, marginBottom: 6 },
  successSub: { fontSize: 13, color: '#888', marginBottom: 16 },
  nextSteps: { fontSize: 13, color: '#666', background: '#0f0f18', borderRadius: 8, padding: '12px 16px', lineHeight: 1.7 },
};
